import { action, makeObservable, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import type { Dnd } from '../../../features/editing/types';
import type { RigsChartDataModel } from '../../../features/rigs-chart/data/rigs-chart-data-model';
import type { Range } from '../../../layers/model';
import type { NeighborScheme } from '../../../shared/entities/neighbor-scheme';
import type { RawWell } from '../../../types';

import type { PadRigOperation } from './pad-rig-operation';

/** Well rig operation on rigs chart. */
export class WellRigOperation
  implements Dnd.Draggable<WellRigOperation, PadRigOperation>, RigsChartDataModel.IChartWellRigOperation
{
  readonly uniqueCreationKey: string;
  readonly geoTaskId: number;
  readonly id: number;
  readonly data: RawWell;
  readonly parentRig: RigsChartDataModel.IChartRig;

  start: number | null;
  end: number | null;
  parentPad?: PadRigOperation;
  neighborScheme?: NeighborScheme<WellRigOperation>;

  @observable y: Range<number> = { start: 0, end: 0 };

  constructor(
    item: RawWell,
    id: number,
    rigOperationId: number,
    startDate: number | null,
    endDate: number | null,
    parentRig: RigsChartDataModel.IChartRig,
    parentPad?: PadRigOperation,
    y?: Range<number>,
  ) {
    this.data = item;
    this.geoTaskId = id;
    this.id = rigOperationId;
    this.uniqueCreationKey = uuidv4();
    this.parentRig = parentRig;
    this.parentPad = parentPad;
    this.start = startDate;
    this.end = endDate;

    if (y) {
      this.setY(y);
    }

    makeObservable(this);
  }
  getShadow?(): PadRigOperation | null {
    throw new Error('Method not implemented.');
  }

  /** Start and end of drilling stage only. */
  get x(): Range<number> | null {
    const { start, end } = this;

    if (!start || !end) {
      return null;
    }

    return {
      start,
      end,
    };
  }

  get title(): string {
    return `Well ${this.geoTaskId}`;
  }

  getKey(prefix?: string): string {
    if (prefix) {
      return `${prefix}-well-${this.uniqueCreationKey}`;
    }

    return `well-${this.uniqueCreationKey}`;
  }

  getFieldValue(fieldName: string): unknown {
    if (fieldName in this.data) {
      return this.data[fieldName];
    }
  }

  @action.bound
  setY(value: Range<number>): void {
    this.y = value;
  }

  setNeighborScheme(scheme: NeighborScheme<WellRigOperation>): void {
    this.neighborScheme = scheme;
  }

  clone(): WellRigOperation {
    return new WellRigOperation(
      this.data,
      this.geoTaskId,
      this.id,
      this.start,
      this.end,
      this.parentRig,
      this.parentPad,
      this.y,
    );
  }
}
